import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QModal,
  QText,
  useToastProvider,
  QModalHeader,
  QModalBody,
  QModalActions,
  QButton,
  QCloseButton,
  QFormControl,
  QBox,
  QSelect,
} from '@qualio/ui-components';
import { QualityEventCreate, User, QualityEvent } from '../../types';
import { excludeCollaborators, filterByPermission } from '../../utils/userUtils';
import { DefaultErrorMessage, OwnerChanged, OwnerNotChanged, SelectANewOwner } from '../../displayStrings';
import { useUsersOptions } from '../../hooks/useUsersOptions';

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  event: QualityEvent;
  selectsData: {
    users: User[];
  };
  onSave: (payload: QualityEventCreate) => Promise<void>;
  isEventUpdateLoading?: boolean;
};

const ChangeOwnerModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  selectsData,
  onSave,
  event,
  isEventUpdateLoading = false,
}) => {
  const { t } = useTranslation();
  const [newOwnerId, setNewOwnerId] = useState<string>(String(event.owner_id));
  const { showToast } = useToastProvider();
  const usersOptions = useUsersOptions(
    excludeCollaborators(filterByPermission(selectsData.users, 'can_work_on_issue'), event.collaborators),
  );

  const handleOwnerChange = useCallback(async () => {
    try {
      await onSave({
        ...event,
        workflow_id: String(event.workflow_id),
        severity_id: event.severity_id ? String(event.severity_id) : undefined,
        probability_id: event.probability_id ? String(event.probability_id) : undefined,
        risk_id: event.risk_id ? String(event.risk_id) : undefined,
        owner_id: newOwnerId,
        tags_ids: event.tags_ids?.map(String),
        escalated_to: undefined,
        escalated_from: undefined,
        collaborators: undefined,
      });
      setIsOpen(false);
      showToast({
        title: OwnerChanged,
        description: t('common.notifications.ownerSuccessfullyChanged', { eventCode: event.code }),
        status: 'success',
        'data-cy': 'qe-event-owner-change-success-toast',
      });
    } catch {
      showToast({
        title: OwnerNotChanged,
        description: DefaultErrorMessage,
        status: 'error',
      });
    }
  }, [onSave, showToast, setIsOpen, event, newOwnerId, t]);

  return (
    <QModal data-cy={'change-owner-modal'} isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
      <QModalHeader>
        <QText>{SelectANewOwner}</QText>
        <QCloseButton onClick={() => setIsOpen(false)}></QCloseButton>
      </QModalHeader>
      <QModalBody>
        <QFormControl label={t('common.labels.owner')} isRequired>
          <QBox data-cy="change-owner-select">
            <QSelect
              value={newOwnerId}
              onChange={(changeObject) => changeObject && setNewOwnerId(changeObject.value)}
              options={usersOptions}
            />
          </QBox>
        </QFormControl>
      </QModalBody>
      <QModalActions>
        <QButton onClick={() => setIsOpen(false)} variant="outline" isDisabled={isEventUpdateLoading}>
          {t('common.actions.cancel')}
        </QButton>
        <QButton onClick={handleOwnerChange} isLoading={isEventUpdateLoading}>
          {t('events.actions.changeOwner')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default ChangeOwnerModal;
